import type { ReactNode, ReactElement } from 'react'
import { useEffect, useState, useCallback, useMemo } from 'react'
import { ThemeContext, type ThemeContextValue } from '../contexts/ThemeContext'
import { applyTheme, toggleTheme as jsToggleTheme, initialize, onThemeChange, getSavedTheme, isDarkTheme, isLightTheme } from '@bizfirst/common-js'
import type { ThemeName } from '@bizfirst/common-js'

export interface ThemeProviderProps {
  children: ReactNode
  defaultTheme?: ThemeName
}

/**
 * ThemeProvider Component
 *
 * Wraps your React app to provide theme management via Context.
 * Automatically initializes theme from localStorage and manages persistence.
 *
 * @example
 * ```tsx
 * <ThemeProvider defaultTheme="dark">
 *   <App />
 * </ThemeProvider>
 * ```
 */
export function ThemeProvider({
  children,
  defaultTheme = 'dark',
}: ThemeProviderProps): ReactElement {
  // Initialize theme from localStorage (respects user's saved preference)
  const [theme, setThemeState] = useState<ThemeName>(() => {
    try {
      // Check localStorage first, then fall back to defaultTheme
      return getSavedTheme() || defaultTheme
    } catch {
      return defaultTheme
    }
  })

  useEffect(() => {
    // Initialize theme system (applies to DOM and saves to localStorage)
    const initialTheme = initialize(defaultTheme)
    setThemeState(initialTheme)
  }, [defaultTheme])

  useEffect(() => {
    // Listen for external theme changes
    const unsubscribe = onThemeChange((newTheme) => {
      setThemeState(newTheme)
    })
    return unsubscribe
  }, [])

  const setTheme = useCallback((newTheme: ThemeName) => {
    applyTheme(newTheme)
    setThemeState(newTheme)
  }, [])

  const toggleTheme = useCallback(() => {
    const newTheme = jsToggleTheme()
    setThemeState(newTheme)
  }, [])

  const value: ThemeContextValue = useMemo(
    () => ({
      theme,
      setTheme,
      toggleTheme,
      isDark: isDarkTheme(),
      isLight: isLightTheme(),
    }),
    [theme, setTheme, toggleTheme]
  )

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}

export default ThemeProvider
